// Cliente HTTP para la API de Consultek (Laravel + Sanctum en modo SPA por cookies).
// El navegador comparte la cookie de sesión entre app.* y api.* (mismo dominio raíz),
// por eso todas las peticiones van con `credentials: "include"`.

const API_URL: string =
  import.meta.env.VITE_API_URL ?? "http://api.consultek.localhost";

/**
 * URL absoluta de un recurso del API, para los casos en que el navegador tiene
 * que ir SOLO — un `<a href>` a un PDF, por ejemplo.
 *
 * No sirve `apiFetch` ahí: descargar con fetch obliga a leer el binario a
 * memoria, envolverlo en un blob y sintetizar un click, y todo eso para
 * terminar haciendo lo que el navegador ya sabe hacer con un enlace. Como la
 * cookie de sesión se comparte entre `app.*` y `api.*` (mismo dominio raíz) y
 * `SameSite=Lax` permite navegaciones GET de primer nivel, el enlace viaja
 * autenticado sin más.
 */
export function urlDelApi(path: string): string {
  return `${API_URL}${path}`;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`API ${status}`);
    this.name = "ApiError";
  }
}

function readCookie(name: string): string | null {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
  const match = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

// Laravel responde 419 cuando el token CSRF falta o no coincide (cookie vieja,
// sesión reciclada o store de sesión reseteado en dev).
const CSRF_TOKEN_MISMATCH = 419;

// Sanctum: obtiene la cookie XSRF-TOKEN. Necesaria antes de mutar estado.
export async function ensureCsrfCookie(): Promise<void> {
  await fetch(`${API_URL}/sanctum/csrf-cookie`, { credentials: "include" });
}

// Envía la petición leyendo el token CSRF FRESCO de la cookie en cada intento
// (tras un refetch, el reintento toma el token nuevo).
function sendRequest(path: string, options: RequestInit): Promise<Response> {
  const xsrf = readCookie("XSRF-TOKEN");

  // Con `FormData` el `Content-Type` NO se declara: lo pone el navegador, y
  // tiene que ponerlo él porque incluye el `boundary` que separa las partes
  // del multipart —una cadena aleatoria que solo él conoce—. Fijarlo a mano
  // manda un `multipart/form-data` sin boundary, y del otro lado PHP no
  // encuentra dónde empieza cada parte: `$_FILES` llega VACÍO y la validación
  // responde "la imagen es obligatoria" sobre una imagen que sí viajó.
  const esFormData = options.body instanceof FormData;

  return fetch(`${API_URL}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(esFormData ? {} : { "Content-Type": "application/json" }),
      ...(xsrf ? { "X-XSRF-TOKEN": xsrf } : {}),
      ...options.headers,
    },
  });
}

/**
 * Extrae un mensaje presentable del body de un ApiError (Laravel).
 * Si el body no trae `message` (o no es JSON), devuelve `fallback`.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    try {
      const parsed = JSON.parse(error.body) as { message?: string };
      if (parsed.message) return parsed.message;
    } catch {
      // Cuerpo no-JSON (p. ej. HTML de error): cae al mensaje genérico.
    }
  }
  return fallback;
}

/**
 * Parsea los errores de validación de un 422 de Laravel
 * (`{ message, errors: { campo: [mensajes] } }`) a `{ campo: primer mensaje }`,
 * listo para `form.setError`. Devuelve `null` si no es un 422 con errores.
 */
export function parseValidationErrors(error: unknown): Record<string, string> | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null;
  try {
    const parsed = JSON.parse(error.body) as { errors?: Record<string, string[]> };
    if (!parsed.errors) return null;
    const entries = Object.entries(parsed.errors)
      .map(([field, messages]) => [field, messages[0]] as const)
      .filter((entry): entry is [string, string] => Boolean(entry[1]));
    return entries.length ? Object.fromEntries(entries) : null;
  } catch {
    return null;
  }
}

/**
 * Descarga un ARCHIVO del API y lo guarda en el equipo del usuario.
 *
 * POR QUÉ NO ES UN `<a href download>`, que sería lo obvio: **el atributo
 * `download` se IGNORA cuando el enlace apunta a otro origen**. El webapp vive
 * en `app.consultek.localhost` y el API en `api.consultek.localhost`, así que
 * un enlace directo no descarga — navega. Eso es lo que hacía el botón "Abrir
 * en otra pestaña", y por eso no funcionaba.
 *
 * Con el binario en memoria, el `blob:` que se crea SÍ es del propio origen y
 * el `download` manda. Cuesta tener el archivo en RAM un instante —el PDF de
 * cotización pesa cerca de un mega— y es el precio de que descargue de verdad.
 *
 * El NOMBRE sale del `Content-Disposition` que manda el servidor: es el mismo
 * que lleva el adjunto del correo al cliente, y armarlo acá sería un segundo
 * lugar donde se decide cómo se llama el documento.
 */
export async function descargarArchivo(path: string, nombrePorDefecto: string): Promise<void> {
  const response = await sendRequest(path, {});

  if (!response.ok) {
    throw new ApiError(response.status, await response.text());
  }

  const url = URL.createObjectURL(await response.blob());

  try {
    const enlace = document.createElement("a");
    enlace.href = url;
    enlace.download = nombreDelArchivo(response) ?? nombrePorDefecto;
    // Sin agregarlo al documento, Firefox ignora el click sintético.
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
  } finally {
    // Sin esto el blob queda retenido hasta que se cierre la pestaña, y son
    // megabytes por descarga.
    URL.revokeObjectURL(url);
  }
}

/**
 * Trae un ARCHIVO del API como `Blob`, para embeberlo (un visor de PDF desde un
 * `blob:` del propio origen) en vez de guardarlo. Mismo motivo que
 * `descargarArchivo`: un enlace directo cruzaría de origen.
 */
export async function traerArchivo(path: string): Promise<Blob> {
  const response = await sendRequest(path, {});

  if (!response.ok) {
    throw new ApiError(response.status, await response.text());
  }

  return response.blob();
}

/** El nombre que el servidor le puso al archivo, o `null` si no lo dijo. */
function nombreDelArchivo(response: Response): string | null {
  const cabecera = response.headers.get("Content-Disposition");
  if (!cabecera) return null;

  // `filename="cotizacion-fundo-x-v1.pdf"`. Sin `filename*` porque el nombre
  // ya viaja en ASCII: `GeneradorDePdfDeCotizacion` lo pasa por `Str::slug`
  // justamente para que ningún cliente lo rompa.
  const coincidencia = /filename="?([^";]+)"?/i.exec(cabecera);
  return coincidencia?.[1]?.trim() || null;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const mutates = !["GET", "HEAD", "OPTIONS"].includes(method);

  // Para peticiones que mutan, aseguramos tener el token CSRF primero.
  if (mutates && !readCookie("XSRF-TOKEN")) {
    await ensureCsrfCookie();
  }

  let response = await sendRequest(path, options);

  // Recuperación transparente ante 419: el middleware VerifyCsrfToken corre
  // ANTES del controlador, así que un 419 nunca ejecutó la mutación —
  // reintentar con token fresco es seguro (no duplica). Un solo reintento:
  // si vuelve a fallar, la sesión está realmente muerta y se propaga el error.
  if (response.status === CSRF_TOKEN_MISMATCH && mutates) {
    await ensureCsrfCookie();
    response = await sendRequest(path, options);
  }

  if (!response.ok) {
    throw new ApiError(response.status, await response.text());
  }

  return response.status === 204
    ? (undefined as T)
    : ((await response.json()) as T);
}
